import { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { observable } from 'mobx';

import { RATE_TYPES } from '@ratehub/documents/mtg-journey-document/stable';
import {
    SCENARIO_TYPES,
    RATE_TYPE_MESSAGES,
    MORTGAGE_TERMS,
    MORTGAGE_TERMS_ALL,
    MORTGAGE_TERM_MESSAGES,
} from '@ratehub/mtg-common';
import { PROVINCE_OPTIONS, getProvincePrincipalCity, getLanguageFromLocale, PROVINCE } from '@ratehub/base-ui';

import { Messages as SCENARIO_TYPE_MESSAGES, MORTGAGE_TYPES } from '../definitions/MortgageTerms';
import fetchRatesForMortgageTable from '../functions/fetchRatesForMortgageTable';


const FIELD_NAME_TERM = 'term';
const FIELD_NAME_TYPE = 'type';
const FIELD_NAME_SCENARIO = 'scenario';

function useMortgageRatesFilters(config) {
    const intl = useIntl();
    const language = getLanguageFromLocale(intl.locale);

    // Stops initial reload triggered by Location Selector useEffect
    let isRefetchEnabled = false;

    const [ self ] = useState(() => observable({
        isFetching: false,
        params: {
            term: config.term,
            type: config.type,
            scenario: config.scenario,
            province: config.province,
            city: config.city,
            language: language,
        },
        rates: config.rates,
        lastUpdatedDate: config.lastUpdatedDate,

        get shouldShowSpinner() {
            return self.isFetching;
        },

        get hasInitialCity() {
            // Checks initial city value, not current value
            return config.city;
        },

        get isProvinceLocked() {
            // Checks initial province value, not current value
            return PROVINCE_OPTIONS.some(province => province.value === config.province);
        },

        get isScenarioHeloc() {
            // Checks initial scenario value, not current value
            return config.scenario === SCENARIO_TYPES.HELOC;
        },

        get filters() {
            return createFilterGroupFields(self.params, intl);
        },

        get selectedCity() {
            const useFallbackCity = !self.hasInitialCity && !self.isProvinceLocked;

            // If there is a city, use that
            // If there is no city, check for a valid province and use the principal city
            // If there is no province, assume Ontario and use the principal city
            return self.params.city ?? getProvincePrincipalCity(
                useFallbackCity
                    ? PROVINCE.ONTARIO
                    : self.params.province,
            );
        },

        async updateParams(values) {
            if (!isRefetchEnabled) {
                return;
            }

            // Location selector
            if (values?.province) {
                self.params = {
                    ...self.params,
                    province: values.province.code,
                    city: values,
                };
            // Filter Group values
            } else if (values) {
                self.params = {
                    ...self.params,
                    term: self.isScenarioHeloc
                        ? self.params.term
                        : values[FIELD_NAME_TERM] ?? MORTGAGE_TERMS[4], // When switching from HELOC we may need to restore default value
                    type: self.isScenarioHeloc
                        ? self.params.type
                        : values[FIELD_NAME_TYPE] ?? MORTGAGE_TYPES.FIXED, // When switching from HELOC we may need to restore default value
                    scenario: values[FIELD_NAME_SCENARIO],
                };
            }

            // Now go fetch some results using the updated params
            self.isFetching = true;

            try {
                self.rates = await fetchRatesForMortgageTable({
                    ...self.params,
    
                    // City & Province are REQUIRED by the API.
                    // These will be empty if GeoIP fails (often because they're outside of Canada)
                    // Just assume some defaults
                    city: self.params.city?.name ?? getProvincePrincipalCity(self.params.province ?? PROVINCE.ONTARIO).name,
                    province: self.params.province ?? PROVINCE.ONTARIO,
                });
            } catch {
                // We don't have to do anything with the error since
                //  the fetch function above will noticeError for us
                self.rates = [];
            }


            self.isFetching = false;
        },
    }));

    // Only run this on mount
    useEffect(() => {
        isRefetchEnabled = true;
    }, []);

    return self;
}

function createFilterGroupFields(params, intl) {
    const MortgageTerms = MORTGAGE_TERMS.map(term => ({
        label: intl.formatMessage(MESSAGES.MORTGAGE_TERM_LABEL, { term: term / 12 }),
        value: term.toString(),
    }));

    MortgageTerms.unshift({
        label: intl.formatMessage(MORTGAGE_TERM_MESSAGES[MORTGAGE_TERMS_ALL]).toLowerCase(),
        value: MORTGAGE_TERMS_ALL.toLowerCase(),
    });

    const MortgageTypes = Object.values(RATE_TYPES).map(type => ({
        label: intl.formatMessage(RATE_TYPE_MESSAGES[type]).toLowerCase(),
        value: type.toLowerCase(),
    }));

    const MortgageScenarioOptions = Object.values(SCENARIO_TYPES)
        // Business requirement - Product tables don't offer pre-approval filter
        // Use web-component scenarios to exclude
        .filter(value => typeof SCENARIO_TYPE_MESSAGES[value] !== 'undefined') // remove unapplicable messages
        .map(value => ({
            label: intl.formatMessage(SCENARIO_TYPE_MESSAGES[value]).toLocaleLowerCase(),
            value: value,
        }));

    const scenarioField = {
        name: FIELD_NAME_SCENARIO,
        options: MortgageScenarioOptions,
        value: params.scenario.toLocaleLowerCase(),
    };

    if (params.scenario === SCENARIO_TYPES.HELOC) {
        return [
            {
                ...scenarioField,
                leftHandText: intl.formatMessage(MESSAGES.HELOC_FIELD_ONE_LEFT_TEXT),
            },
        ];
    } else {
        return [
            {
                name: FIELD_NAME_TERM,
                options: MortgageTerms,
                value: params?.term.toString().toLocaleLowerCase(),
                leftHandText: intl.formatMessage(MESSAGES.FIELD_ONE_LEFT_TEXT),
            },
            {
                name: FIELD_NAME_TYPE,
                options: MortgageTypes,
                value: params?.type.toLocaleLowerCase(),
                rightHandText: intl.formatMessage(MESSAGES.FIELD_TWO_RIGHT_TEXT),
            },
            scenarioField,
        ];
    }
}

const MESSAGES = defineMessages({
    LAST_UPDATED: {
        id: 'web-components.mortgageRateTable.lastUpdatedText',
        defaultMessage: 'Last updated:',
    },
    FIELD_ONE_LEFT_TEXT: {
        id: 'web-components.mortgageRateTable.fieldOneLeftText',
        defaultMessage: 'These are the best',
    },
    FIELD_TWO_RIGHT_TEXT: {
        id: 'web-components.mortgageRateTable.fieldTwoRightText',
        defaultMessage: 'mortgage rates for',
    },
    HELOC_FIELD_ONE_LEFT_TEXT: {
        id: 'web-components.mortgageRateTable.fieldTwoRightTextHeloc',
        defaultMessage: 'Mortgage rates for',
    },
    MORTGAGE_TERM_LABEL: {
        id: 'web-components.mortgageRateTable.mortgageTermLabel',
        defaultMessage: '{term, select, 1 {1-year} other {{term}-year}}',
    },
});

export default useMortgageRatesFilters;
